import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appLockApi, APP_LOCK_DOMAIN, type AppLockSettings } from "./appLockApi";
import { sendMessage } from "utils/webview";

export const APP_LOCK_QUERY_KEY = [APP_LOCK_DOMAIN, "settings"] as const;

export function useAppLockSettings() {
  return useQuery({
    queryKey: APP_LOCK_QUERY_KEY,
    queryFn: () => appLockApi.get(),
  });
}

export function useEnableAppLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => appLockApi.enable(),
    onSuccess: (data: AppLockSettings) => {
      const prev = queryClient.getQueryData<AppLockSettings>(APP_LOCK_QUERY_KEY);
      if (!prev || prev.isEnabled !== data.isEnabled) {
        try {
          sendMessage(
            JSON.stringify({
              event: "set_app_lock",
              params: { isEnabled: data.isEnabled ? "true" : "false" },
            })
          );
        } catch {}
      }
      queryClient.setQueryData(APP_LOCK_QUERY_KEY, data);
    },
  });
}

export function useDisableAppLock() {
  const queryClient = useQueryClient();
  return useMutation({
    // Disable must also remove PIN on the server
    mutationFn: () => appLockApi.removePin(),
    onSuccess: (data: AppLockSettings) => {
      const prev = queryClient.getQueryData<AppLockSettings>(APP_LOCK_QUERY_KEY);
      if (!prev || prev.isEnabled !== data.isEnabled) {
        try {
          sendMessage(
            JSON.stringify({
              event: "set_app_lock",
              params: { isEnabled: data.isEnabled ? "true" : "false" },
            })
          );
        } catch {}
      }
      queryClient.setQueryData(APP_LOCK_QUERY_KEY, data);
    },
  });
}

export function useSetAppLockPin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pinHash, isEnable = true }: { pinHash: string; isEnable?: boolean }) =>
      appLockApi.setPin(pinHash, isEnable),
    onSuccess: (data: AppLockSettings) => {
      const prev = queryClient.getQueryData<AppLockSettings>(APP_LOCK_QUERY_KEY);
      if (!prev || prev.isEnabled !== data.isEnabled) {
        try {
          sendMessage(
            JSON.stringify({
              event: "set_app_lock",
              params: { isEnabled: data.isEnabled ? "true" : "false" },
            })
          );
        } catch {}
      }
      queryClient.setQueryData(APP_LOCK_QUERY_KEY, data);
    },
  });
}

export function useRemoveAppLockPin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => appLockApi.removePin(),
    onSuccess: (data: AppLockSettings) => {
      const prev = queryClient.getQueryData<AppLockSettings>(APP_LOCK_QUERY_KEY);
      if (!prev || prev.isEnabled !== data.isEnabled) {
        try {
          sendMessage(
            JSON.stringify({
              event: "set_app_lock",
              params: { isEnabled: data.isEnabled ? "true" : "false" },
            })
          );
        } catch {}
      }
      queryClient.setQueryData(APP_LOCK_QUERY_KEY, data);
    },
  });
}

export function useUpdateBiometricEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isBiometricEnabled: boolean) => appLockApi.updateSettings({ isBiometricEnabled: isBiometricEnabled }),
    onSuccess: (data: AppLockSettings) => {
      const prev = queryClient.getQueryData<AppLockSettings>(APP_LOCK_QUERY_KEY);
      if (!prev || prev.isEnabled !== data.isEnabled) {
        try {
          sendMessage(
            JSON.stringify({
              event: "set_app_lock",
              params: { isEnabled: data.isEnabled ? "true" : "false" },
            })
          );
        } catch {}
      }
      queryClient.setQueryData(APP_LOCK_QUERY_KEY, data);
    },
  });
}
